import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import UpdateProductForm from "./UpdateProductForm";
import Graph from "./Graph";
import { showErrorToast, showSuccessToast } from "../../utils/toaster";
import { baseurl } from "../../config/url";
import Swal from "sweetalert2";

const ProductHistory = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeProductId, setActiveProductId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [categoriesError, setCategoriesError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("sellerToken") ||
    localStorage.getItem("adminToken");

  const axiosInstance = useMemo(() => {
    return axios.create({
      baseURL: baseurl,
      timeout: 20000,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }, []);

  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.request.use(
      (config) => {
        const latestToken =
          localStorage.getItem("token") ||
          localStorage.getItem("sellerToken") ||
          localStorage.getItem("adminToken");

        if (latestToken) {
          config.headers.Authorization = `Bearer ${latestToken}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axiosInstance.interceptors.request.eject(interceptorId);
    };
  }, [axiosInstance]);

  const formatMoney = (n) => {
    const num = Number(n || 0);
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const getFallbackImage = (text = "No image") =>
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(`
      <svg xmlns='http://www.w3.org/2000/svg' width='420' height='260'>
        <rect width='100%' height='100%' fill='#f3f4f6'/>
        <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#6b7280' font-family='Arial' font-size='14'>
          ${text}
        </text>
      </svg>
    `);

  const getImageUrl = (img) => {
    if (!img) return getFallbackImage("No image");

    if (typeof img === "string") {
      if (img.startsWith("http://") || img.startsWith("https://")) return img;

      const cleanBase = (baseurl || "").replace(/\/+$/, "");
      const cleanImg = img.startsWith("/") ? img : `/${img}`;
      return `${cleanBase}${cleanImg}`;
    }

    if (typeof img === "object" && img?.url) {
      if (img.url.startsWith("http://") || img.url.startsWith("https://")) {
        return img.url;
      }

      const cleanBase = (baseurl || "").replace(/\/+$/, "");
      const cleanImg = img.url.startsWith("/") ? img.url : `/${img.url}`;
      return `${cleanBase}${cleanImg}`;
    }

    return getFallbackImage("Image unavailable");
  };

  const badge = (label, tone = "gray") => {
    const tones = {
      gray: "bg-gray-50 text-gray-700 border-gray-200",
      blue: "bg-blue-50 text-blue-700 border-blue-200",
      green: "bg-green-50 text-green-700 border-green-200",
      red: "bg-red-50 text-red-700 border-red-200",
      orange: "bg-orange-50 text-orange-700 border-orange-200",
    };

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
          tones[tone] || tones.gray
        }`}
      >
        {label}
      </span>
    );
  };

  const getAllCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      setCategoriesError("");

      const response = await axiosInstance.get("/api/category/get-all");
      const data = Array.isArray(response?.data) ? response.data : [];

      setCategories(data);
    } catch (error) {
      console.error(
        "Error fetching categories:",
        error?.response?.data || error.message
      );
      setCategories([]);
      setCategoriesError("Failed to load categories.");
      showErrorToast("Failed to load categories.");
    } finally {
      setLoadingCategories(false);
    }
  }, [axiosInstance]);

  const getAllProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      setProductsError("");

      const response = await axiosInstance.get("/api/seller/get-all-products");
      const data = Array.isArray(response?.data) ? response.data : [];

      setProducts(data);

      if (data.length === 0) {
        setSelectedProduct(null);
        setActiveProductId(null);
        setIsEditMode(false);
        return;
      }

      setSelectedProduct((prev) => {
        if (!prev) return data[0];
        const updatedSelected = data.find((item) => item._id === prev._id);
        return updatedSelected || data[0];
      });

      setActiveProductId((prev) => {
        if (!prev) return data[0]._id;
        const exists = data.some((item) => item._id === prev);
        return exists ? prev : data[0]._id;
      });
    } catch (error) {
      console.error(
        "Error fetching products:",
        error?.response?.data || error.message
      );

      let msg = "Failed to load products from server.";

      if (error?.response?.status === 401) {
        msg = "Unauthorized. Please login again.";
      } else if (error?.response?.status === 403) {
        msg = "Access denied for products API.";
      } else if (error?.response?.status === 404) {
        msg = "Products API route not found.";
      } else if (error?.code === "ECONNABORTED") {
        msg = "Request timeout. Please try again.";
      }

      setProducts([]);
      setSelectedProduct(null);
      setActiveProductId(null);
      setIsEditMode(false);
      setProductsError(msg);
      showErrorToast(msg);
    } finally {
      setLoadingProducts(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (!token) {
      showErrorToast("Token not found. Please login first.");
      return;
    }

    getAllCategories();
    getAllProducts();
  }, [token, getAllCategories, getAllProducts]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setActiveProductId(product._id);
    setIsEditMode(false);
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await axiosInstance.delete(`/api/seller/delete-product/${productId}`);

      const updatedProducts = products.filter((p) => p._id !== productId);
      setProducts(updatedProducts);

      if (selectedProduct?._id === productId) {
        setSelectedProduct(updatedProducts[0] || null);
        setActiveProductId(updatedProducts[0]?._id || null);
      }

      setIsEditMode(false);
      showSuccessToast("Product deleted successfully.");
    } catch (error) {
      console.error(
        "Delete product error:",
        error?.response?.data || error.message
      );
      showErrorToast("Failed to delete product. Please try again.");
    }
  };

  const handleDelete = (productId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this product? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteProduct(productId);
      }
    });
  };

  const filteredProducts = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return products.filter((product) => {
      const matchesSearch =
        product?.name?.toLowerCase().includes(q) ||
        product?.category?.name?.toLowerCase().includes(q) ||
        product?.subcategory?.name?.toLowerCase().includes(q);

      const matchesCategory =
        categoryFilter === "all" || product?.category?._id === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [products, searchQuery, categoryFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / itemsPerPage)
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedProducts = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  useEffect(() => {
    if (!filteredProducts.length) {
      setSelectedProduct(null);
      setActiveProductId(null);
      setIsEditMode(false);
      return;
    }

    const stillExists = filteredProducts.find((p) => p._id === activeProductId);

    if (!stillExists) {
      setSelectedProduct(filteredProducts[0]);
      setActiveProductId(filteredProducts[0]._id);
      setIsEditMode(false);
    }
  }, [filteredProducts, activeProductId]);

  return (
    <div className="p-4 md:p-6">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              Product History
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Browse your uploaded products, view details, and update or delete
              items.
            </p>
             
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {badge(`Total: ${products.length}`, "blue")}
            {badge(`Filtered: ${filteredProducts.length}`, "gray")}
            {!token && badge("No Token", "red")}
          </div>
        </div>
        
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-600">
                  Search Product
                </label>
                <input
                  type="text"
                  placeholder="Search by product, category, subcategory..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div className="w-full md:w-72">
                <label className="text-xs font-semibold text-gray-600">
                  Category Filter
                </label>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="all">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {loadingCategories && (
                  <p className="text-xs text-gray-500 mt-2">
                    Loading categories...
                  </p>
                )}
                {categoriesError && (
                  <p className="text-xs text-red-600 mt-2">{categoriesError}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            {loadingProducts ? (
              <div className="p-10 text-center text-gray-600">
                Loading products...
              </div>
            ) : productsError ? (
              <div className="p-10 text-center">
                <p className="text-red-600 font-semibold">{productsError}</p>
              </div>
            ) : products.length === 0 ? (
              <div className="p-10 text-center text-gray-600">
                No products found.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                      <tr className="text-left">
                        <th className="px-4 md:px-6 py-3 text-xs font-bold text-gray-600 uppercase tracking-wide">
                          Product
                        </th>
                        <th className="px-4 md:px-6 py-3 text-xs font-bold text-gray-600 uppercase tracking-wide text-center">
                          Sold
                        </th>
                        <th className="px-4 md:px-6 py-3 text-xs font-bold text-gray-600 uppercase tracking-wide text-center">
                          Price
                        </th>
                        <th className="px-4 md:px-6 py-3 text-xs font-bold text-gray-600 uppercase tracking-wide text-center">
                          Size
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {filteredProducts.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-6 py-10 text-center text-gray-600"
                          >
                            {searchQuery.trim()
                              ? "No product found"
                              : "No products match the current filters"}
                          </td>
                        </tr>
                      ) : (
                        paginatedProducts.map((product, index) => {
                          const isActive = activeProductId === product._id;

                          return (
                            <tr
                              key={product._id || index}
                              onClick={() => handleProductClick(product)}
                              className={[
                                "cursor-pointer transition",
                                isActive ? "bg-blue-50" : "bg-white",
                                "hover:bg-gray-50",
                              ].join(" ")}
                            >
                              <td className="px-4 md:px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div
                                    className={[
                                      "w-2.5 h-2.5 rounded-full",
                                      isActive ? "bg-blue-600" : "bg-gray-300",
                                    ].join(" ")}
                                  />
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">
                                      {product?.name || "—"}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {product?.category?.name || "—"} •{" "}
                                      {product?.subcategory?.name || "—"}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 md:px-6 py-4 text-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-700">
                                  {product?.sold ?? 0}
                                </span>
                              </td>

                              <td className="px-4 md:px-6 py-4 text-center text-sm font-semibold text-gray-900">
                                {formatMoney(product?.price)}
                              </td>

                              <td className="px-4 md:px-6 py-4 text-center text-sm text-gray-700">
                                {product?.size || "—"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 md:px-6 py-4 border-t border-gray-200 bg-white">
                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold text-gray-900">
                      {filteredProducts.length === 0 ? 0 : indexOfFirstItem + 1}
                    </span>{" "}
                    –{" "}
                    <span className="font-semibold text-gray-900">
                      {Math.min(indexOfLastItem, filteredProducts.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-900">
                      {filteredProducts.length}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition"
                    >
                      Previous
                    </button>

                    <span className="text-sm text-gray-700 px-3">
                      Page{" "}
                      <span className="font-semibold text-gray-900">
                        {currentPage}
                      </span>{" "}
                      of{" "}
                      <span className="font-semibold text-gray-900">
                        {totalPages}
                      </span>
                    </span>

                    <button
                      onClick={() =>
                        setCurrentPage((prev) =>
                          prev < totalPages ? prev + 1 : prev
                        )
                      }
                      disabled={currentPage >= totalPages}
                      className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 md:p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                Analytics
              </h3>
              <span className="text-xs text-gray-500">
                (Graph component as per your logic)
              </span>
            </div>
            <div className="mt-3">
              <Graph salesData={salesData} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          {selectedProduct && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden lg:sticky lg:top-6">
              {!isEditMode ? (
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 truncate">
                        {selectedProduct?.name || "Product Details"}
                      </h3>

                      <div className="mt-2 flex flex-wrap gap-2">
                        {badge(selectedProduct?.saleType || "—", "blue")}
                        {badge(
                          selectedProduct?.approved
                            ? "Approved"
                            : "Not Approved",
                          selectedProduct?.approved ? "green" : "orange"
                        )}
                        {selectedProduct?.visibilityLevel
                          ? badge(
                              `Visibility: ${selectedProduct.visibilityLevel}`,
                              "gray"
                            )
                          : badge("Visibility: —", "gray")}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
                    <img
                      src={getImageUrl(selectedProduct?.image)}
                      alt={selectedProduct?.name || "Product image"}
                      className="w-full h-52 object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          getFallbackImage("Image unavailable");
                      }}
                    />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <div className="text-xs text-gray-500 font-semibold">
                        Price
                      </div>
                      <div className="text-sm font-bold text-gray-900 mt-1">
                        {formatMoney(selectedProduct?.price)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <div className="text-xs text-gray-500 font-semibold">
                        Sold
                      </div>
                      <div className="text-sm font-bold text-gray-900 mt-1">
                        {selectedProduct?.sold ?? 0}
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <div className="text-xs text-gray-500 font-semibold">
                        Stock
                      </div>
                      <div className="text-sm font-bold text-gray-900 mt-1">
                        {selectedProduct?.stock ?? 0}
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <div className="text-xs text-gray-500 font-semibold">
                        Size
                      </div>
                      <div className="text-sm font-bold text-gray-900 mt-1">
                        {selectedProduct?.size || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="text-sm font-semibold text-gray-900">
                        Details
                      </div>
                    </div>

                    <div className="p-4 space-y-2 text-sm text-gray-700">
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Category</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProduct?.category?.name || "—"}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Subcategory</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProduct?.subcategory?.name || "—"}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Min Qty</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProduct?.minquantity ?? "—"}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Max Qty</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProduct?.maxquantity ?? "—"}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">GST</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProduct?.gstNumber ?? "—"}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">HSN</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProduct?.hsnCode ?? "—"}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Delivery</span>
                        <span className="font-semibold text-gray-900">
                          {selectedProduct?.deliveryPreference?.duration ??
                            "—"}{" "}
                          {selectedProduct?.deliveryPreference?.unit ?? ""}
                        </span>
                      </div>

                      <div className="pt-2">
                        <div className="text-gray-500 text-xs font-semibold">
                          Description
                        </div>
                        <div className="text-sm text-gray-700 mt-1 leading-relaxed">
                          {selectedProduct?.description || "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900">
                        Ratings
                      </div>
                      <span className="text-xs text-gray-500">
                        Total: {selectedProduct?.totalratings ?? 0}
                      </span>
                    </div>

                    <div className="mt-2 text-sm text-gray-700">
                      <span className="text-gray-500">Average: </span>
                      <span className="font-semibold text-gray-900">
                        {Array.isArray(selectedProduct?.ratings) &&
                        selectedProduct.ratings.length > 0
                          ? (
                              selectedProduct.ratings.reduce(
                                (sum, rating) => sum + Number(rating || 0),
                                0
                              ) / selectedProduct.ratings.length
                            ).toFixed(1)
                          : "Not Available"}
                      </span>
                    </div>
                  </div>

                  {selectedProduct?.specifications?.length > 0 && (
                    <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div className="text-sm font-semibold text-gray-900">
                          Specifications
                        </div>
                      </div>

                      <div className="p-4 space-y-2">
                        {selectedProduct.specifications.map((spec) => (
                          <div
                            key={spec._id}
                            className="flex items-start justify-between gap-3 text-sm"
                          >
                            <span className="font-semibold text-gray-900">
                              {spec.key}
                            </span>
                            <span className="text-gray-700 text-right">
                              {spec.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setIsEditMode(true)}
                      className="px-4 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-green-200"
                    >
                      Update
                    </button>

                    <button
                      onClick={() => handleDelete(selectedProduct._id)}
                      className="px-4 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <UpdateProductForm
                  categories={categories}
                  product={selectedProduct}
                  getAllProducts={getAllProducts}
                  handleDeleteProduct={handleDeleteProduct}
                  setIsEditMode={setIsEditMode}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductHistory;




 